import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { ChevronDown, X } from 'lucide-react';

import { Button } from '@/components/ui/Button';
import { dashboardItem, navigation } from '@/constants/navigation';
import type { NavSection } from '@/constants/navigation';
import { cn } from '@/lib/cn';
import { useAuthStore } from '@/store/auth.store';
import { oid } from '@/lib/objectId';

export interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

const COLLAPSED_KEY = 'searah.sidebar.collapsedSections';

function readCollapsed(): string[] {
  try {
    const raw = window.localStorage.getItem(COLLAPSED_KEY);
    const parsed = raw ? (JSON.parse(raw) as unknown) : null;
    return Array.isArray(parsed) ? parsed.filter((value): value is string => typeof value === 'string') : [];
  } catch {
    return [];
  }
}

function navLinkClass({ isActive }: { isActive: boolean }) {
  return cn(
    'flex items-center gap-3 rounded-btn px-3 py-2 text-sm font-medium',
    'transition-colors duration-150 ease-premium',
    isActive ? 'bg-white text-primary shadow-card' : 'text-primary-200 hover:bg-white/10 hover:text-white',
  );
}

function isSectionActive(section: NavSection, pathname: string) {
  return section.items.some((item) =>
    item.to === '/' ? pathname === '/' : pathname === item.to || pathname.startsWith(`${item.to}/`),
  );
}

export function Sidebar({ isOpen, onClose }: SidebarProps) {
  const can = useAuthStore((state) => state.can);
  const { pathname } = useLocation();
  const [collapsed, setCollapsed] = useState<string[]>(readCollapsed);

  const visibleSections = navigation
    .map((section) => ({ ...section, items: section.items.filter((item) => can(item.permission)) }))
    .filter((section) => section.items.length > 0);

  const activeSection = useMemo(
    () => visibleSections.find((section) => isSectionActive(section, pathname))?.title,
    [visibleSections, pathname],
  );

  useEffect(() => {
    if (!activeSection) return;
    setCollapsed((current) => (current.includes(activeSection) ? current.filter((title) => title !== activeSection) : current));
  }, [activeSection]);

  useEffect(() => {
    try {
      window.localStorage.setItem(COLLAPSED_KEY, JSON.stringify(collapsed));
    } catch {
    }
  }, [collapsed]);

  const toggleSection = useCallback((title: string) => {
    setCollapsed((current) =>
      current.includes(title) ? current.filter((item) => item !== title) : [...current, title],
    );
  }, []);

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 z-30 animate-fade-in bg-primary-900/60 lg:hidden"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      <aside
        aria-label="Main navigation"
        className={cn(
          'fixed inset-y-0 left-0 z-40 isolate flex w-[272px] flex-col overflow-hidden bg-[#061A2F] text-white',
          'transition-transform duration-200 ease-premium lg:translate-x-0',
          isOpen ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10">
          <div className="absolute inset-0 bg-gradient-to-b from-[#061A2F] via-[#082E4F] to-[#4A2110]" />

          <div className="absolute -bottom-24 -right-28 h-[420px] w-[420px] rounded-full bg-[#F7921E]/40 blur-[90px]" />
          <div className="absolute -right-36 -top-36 h-[360px] w-[360px] rounded-full bg-[#F7921E]/20 blur-[110px]" />

          <div className="absolute -bottom-40 -left-32 h-[420px] w-[420px] rounded-full bg-[#086CA4]/20 blur-[100px]" />
        </div>

        <svg
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 -z-10 h-full w-full opacity-60"
          viewBox="0 0 300 1200"
          preserveAspectRatio="none"
        >
          <defs>
            <linearGradient id="sidebar-wave-gradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#086CA4" stopOpacity="0" />
              <stop offset="22%" stopColor="#086CA4" stopOpacity="0.75" />
              <stop offset="52%" stopColor="#F7921E" stopOpacity="0.95" />
              <stop offset="80%" stopColor="#F7921E" stopOpacity="0.85" />
              <stop offset="100%" stopColor="#F7921E" stopOpacity="0" />
            </linearGradient>
          </defs>

          <path
            d="M220 -100 C80 120 80 260 180 440 S300 760 130 940 S30 1130 100 1300"
            fill="none"
            stroke="url(#sidebar-wave-gradient)"
            strokeWidth="2"
          />

          <path
            d="M245 -100 C105 120 105 270 205 450 S325 770 155 950 S55 1140 125 1300"
            fill="none"
            stroke="url(#sidebar-wave-gradient)"
            strokeWidth="1.5"
            opacity="0.7"
          />

          <path
            d="M270 -100 C130 120 130 280 230 460 S350 780 180 960 S80 1150 150 1300"
            fill="none"
            stroke="url(#sidebar-wave-gradient)"
            strokeWidth="1"
            opacity="0.5"
          />

          <path
            d="M195 -100 C55 110 55 250 155 430 S275 750 105 930 S5 1120 75 1300"
            fill="none"
            stroke="url(#sidebar-wave-gradient)"
            strokeWidth="1"
            opacity="0.4"
          />
        </svg>

        <div className="flex h-nav shrink-0 items-center justify-between gap-3 border-b border-white/10 px-5">
          <Link
            id="lbl_SidebarLogo"
            to="/"
            onClick={onClose}
            aria-label="Dashboard"
            className="flex w-full items-center justify-center rounded-btn transition-opacity duration-150 ease-premium hover:opacity-80"
          >
            <img src="/Searah-logo-white.png" alt="Searah" className="h-18 w-auto" />
          </Link>

          <Button
            id="btn_CloseNavigation"
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="text-white hover:bg-white/10 hover:text-white lg:hidden"
            aria-label="Close navigation"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </Button>
        </div>

        <nav className="scrollbar-thin flex-1 overflow-y-auto px-3 py-4">
          {can(dashboardItem.permission) && (
            <div className="mb-4">
              <NavLink id={oid('trm_Menu', dashboardItem.label)} to={dashboardItem.to} end onClick={onClose} className={navLinkClass}>
                <dashboardItem.icon className="h-[18px] w-[18px] shrink-0" aria-hidden="true" />
                {dashboardItem.label}
              </NavLink>
            </div>
          )}

          {visibleSections.map((section) => {
            const isCollapsed = collapsed.includes(section.title);
            const panelId = oid('pnl_SidebarSection', section.title);

            return (
              <div key={section.title} className={cn('transition-all', isCollapsed ? 'mb-1.5' : 'mb-5')}>
                <button
                  id={oid('trm_Section', section.title)}
                  type="button"
                  onClick={() => toggleSection(section.title)}
                  aria-expanded={!isCollapsed}
                  aria-controls={panelId}
                  className={cn(
                    'flex w-full items-center justify-between gap-2 rounded-btn px-3 py-1.5',
                    'text-label uppercase text-primary-300',
                    'transition-colors duration-150 ease-premium hover:bg-white/5 hover:text-white',
                  )}
                >
                  <span>{section.title}</span>
                  <ChevronDown
                    className={cn(
                      'h-4 w-4 shrink-0 transition-transform duration-200 ease-premium',
                      isCollapsed && '-rotate-90',
                    )}
                    aria-hidden="true"
                  />
                </button>

                <div
                  id={panelId}
                  className={cn(
                    'grid overflow-hidden transition-[grid-template-rows,opacity] duration-200 ease-premium',
                    isCollapsed ? 'grid-rows-[0fr] opacity-0' : 'grid-rows-[1fr] pt-1 opacity-100',
                  )}
                >
                  <ul className="flex min-h-0 flex-col gap-0.5">
                    {section.items.map((item) => (
                      <li key={item.to}>
                        <NavLink
                          id={oid('trm_Menu', item.label)}
                          to={item.to}
                          end={item.to === '/'}
                          onClick={onClose}
                          tabIndex={isCollapsed ? -1 : undefined}
                          className={navLinkClass}
                        >
                          <item.icon className="h-[18px] w-[18px] shrink-0" aria-hidden="true" />
                          {item.label}
                        </NavLink>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })}
        </nav>
      </aside>
    </>
  );
}
